import moment from "moment";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AirplaneInFlight, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { formatAsMoney } from "../../../helpers/numbers";
import { IntlSearchResult, NormalizedOffer } from "../../../@types/intlFlightAggregator";
import AviationAmbientBackground from "../../../components/AviationAmbientBackground";

const STORAGE_KEY = "intl-flight-v2-search-result";

const formatDuration = (iso: string | null) => {
  if (!iso) return null;
  // ISO 8601 durations: PT6H52M for short-haul, but a long-haul/multi-stop
  // routing over 24h comes back as P1DT4H15M — the days component sits
  // before the T, so it needs its own capture group.
  const match = iso.match(/^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return iso;
  const days = match[1] ? `${match[1]}d` : "";
  const hours = match[2] ? `${match[2]}h` : "";
  const minutes = match[3] ? `${match[3]}m` : "";
  return [days, hours, minutes].filter(Boolean).join(" ") || iso;
};

/**
 * Purely decorative flight-route illustration behind the hero banner — a
 * dashed great-circle-style arc between two waypoints with a dot-grid for
 * texture, kept low-opacity so it reads as ambience, not content.
 */
const HeroIllustration = () => (
  <svg
    viewBox="0 0 900 320"
    fill="none"
    preserveAspectRatio="xMidYMid slice"
    className="absolute inset-0 h-full w-full"
    aria-hidden="true"
  >
    <defs>
      <pattern id="hero-dot-grid" width="26" height="26" patternUnits="userSpaceOnUse">
        <circle cx="1.5" cy="1.5" r="1.5" fill="white" fillOpacity="0.06" />
      </pattern>
    </defs>
    <rect width="900" height="320" fill="url(#hero-dot-grid)" />

    <path
      d="M 40 250 Q 300 40 470 140 T 860 70"
      stroke="white"
      strokeOpacity="0.22"
      strokeWidth="2"
      strokeDasharray="2 10"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="40" cy="250" r="5" fill="#EA5C29" />
    <circle cx="40" cy="250" r="10" stroke="#EA5C29" strokeOpacity="0.4" strokeWidth="1.5" fill="none" />
    <circle cx="860" cy="70" r="5" fill="#EA5C29" />
    <circle cx="860" cy="70" r="10" stroke="#EA5C29" strokeOpacity="0.4" strokeWidth="1.5" fill="none" />

    <g transform="translate(470 140) rotate(-18)" opacity="0.9">
      <path
        d="M0 -9 L2.4 -2.5 L11 0.5 L11 3 L2.4 1.5 L0.8 8 L3.2 9.6 L3.2 11.4 L0 10.2 L-3.2 11.4 L-3.2 9.6 L-0.8 8 L-2.4 1.5 L-11 3 L-11 0.5 L-2.4 -2.5 Z"
        fill="white"
        fillOpacity="0.85"
      />
    </g>

    <path
      d="M 610 260 q 40 -30 80 0 q 40 30 80 0"
      stroke="white"
      strokeOpacity="0.08"
      strokeWidth="18"
      strokeLinecap="round"
      fill="none"
    />
    <path
      d="M 120 60 q 30 -22 60 0 q 30 22 60 0"
      stroke="white"
      strokeOpacity="0.06"
      strokeWidth="14"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const OfferCard = ({ offer, onSelect }: { offer: NormalizedOffer; onSelect: (offer: NormalizedOffer) => void }) => {
  const firstSlice = offer.slices[0];
  const firstSegment = firstSlice?.segments[0];
  const lastSegment = firstSlice?.segments[firstSlice.segments.length - 1];
  const stopCount = firstSlice ? firstSlice.segments.length - 1 : 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(offer)}
      className="group relative flex flex-col h-full w-full text-left bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)] hover:shadow-[0_16px_36px_-8px_rgba(234,92,41,0.25)] hover:border-visaro-orange/40 hover:-translate-y-1.5 transition-all duration-300 ease-out overflow-hidden"
    >
      {/* Accent bar */}
      <div className="absolute top-0 left-0 w-full h-1 bg-visaro-orange scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />

      {/* Airline */}
      <div className="flex items-center justify-between gap-2 p-4 pb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex items-center justify-center size-9 rounded-full bg-deepBlue/5 text-deepBlue shrink-0 transition-colors duration-300 group-hover:bg-visaro-orange group-hover:text-white">
            <AirplaneInFlight size={18} weight="duotone" />
          </span>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-bold text-deepBlue truncate">{firstSegment?.airline ?? "—"}</span>
            <span className="text-xs text-gray-400 truncate">
              {firstSegment?.flight_number ? `Flight ${firstSegment.flight_number}` : offer.supplier}
            </span>
          </div>
        </div>
        {!offer.requires_instant_payment && (
          <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-visaro-visaGreen bg-visaro-visaMintGreen px-2 py-1 rounded-full whitespace-nowrap">
            Hold
          </span>
        )}
      </div>

      {/* Route timeline */}
      <div className="flex items-center gap-2 px-4 pb-4">
        <div className="flex flex-col items-start min-w-[50px]">
          <span className="text-lg font-bold font-aeonic text-deepBlue">{firstSlice?.origin ?? "—"}</span>
          <span className="text-[11px] font-bold text-gray-400">
            {firstSegment?.departure_at ? moment(firstSegment.departure_at).format("HH:mm") : "—"}
          </span>
        </div>

        <div className="flex-1 flex flex-col items-center gap-1 min-w-0 px-1">
          <span className="text-[10px] text-gray-400 font-medium truncate">{formatDuration(firstSlice?.duration ?? null)}</span>
          <div className="flex items-center w-full gap-1">
            <div className="h-[1px] flex-1 bg-gray-200 relative">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-gray-300" />
            </div>
            <AirplaneInFlight className="size-3.5 shrink-0 text-visaro-orange" weight="fill" />
            <div className="h-[1px] flex-1 bg-gray-200 relative">
              <div className="absolute right-0 top-1/2 -translate-y-1/2 size-1.5 rounded-full bg-gray-300" />
            </div>
          </div>
          <span className="text-[10px] text-gray-400 font-medium truncate">
            {stopCount === 0 ? "Direct" : `${stopCount} stop${stopCount > 1 ? "s" : ""}`}
          </span>
        </div>

        <div className="flex flex-col items-end min-w-[50px]">
          <span className="text-lg font-bold font-aeonic text-deepBlue">{firstSlice?.destination ?? "—"}</span>
          <span className="text-[11px] font-bold text-gray-400">
            {lastSegment?.arrival_at ? moment(lastSegment.arrival_at).format("HH:mm") : "—"}
          </span>
        </div>
      </div>

      {/* Price + CTA */}
      <div className="mt-auto flex items-center justify-between gap-2 px-4 py-3.5 bg-gray-50/70 border-t border-gray-100 group-hover:bg-visaro-orange/5 transition-colors duration-300">
        <div className="flex flex-col min-w-0">
          <span className="text-base font-bold text-deepBlue whitespace-nowrap truncate">
            {offer.currency} {offer.total_amount !== null ? formatAsMoney(offer.total_amount) : "—"}
          </span>
          {offer.quote && (
            <span className="text-xs text-gray-400 whitespace-nowrap truncate">≈ ₦{formatAsMoney(offer.quote.amount)}</span>
          )}
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-visaro-orange bg-visaro-orange/10 group-hover:bg-visaro-orange group-hover:text-white group-hover:gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300">
          Select
          <ArrowRight size={12} weight="bold" />
        </span>
      </div>
    </button>
  );
};

const SearchResults = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (state?.searchResult) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.searchResult));
    }
  }, [state?.searchResult]);

  const searchResult: IntlSearchResult | null = useMemo(() => {
    if (state?.searchResult) return state.searchResult as IntlSearchResult;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as IntlSearchResult) : null;
  }, [state?.searchResult]);

  const offers = searchResult?.offers ?? [];

  const onSelectOffer = (offer: NormalizedOffer) => {
    navigate("/aviation/international/v2/review", { state: { offer } });
  };

  if (!searchResult) {
    return (
      <div className="min-h-screen bg-gray-50 p-10 flex flex-col items-center justify-center gap-4 text-center">
        <p className="text-gray-500">No search in progress. Start a new search.</p>
        <button
          type="button"
          onClick={() => navigate("/aviation/international/v2/search")}
          className="bg-visaro-orange text-white px-6 py-3 rounded-lg"
        >
          Search Flights
        </button>
      </div>
    );
  }

  const heroSlice = offers[0]?.slices[0];
  const heroDeparture = heroSlice?.segments[0]?.departure_at;

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      <AviationAmbientBackground />

      <div className="relative p-5 lg:p-10 py-10 flex flex-col gap-8 max-w-6xl mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-deepBlue via-deepBlue to-[#2c2c56] px-6 py-8 lg:px-10 lg:py-10">
          <HeroIllustration />
          <div className="absolute -top-24 -right-16 size-72 rounded-full bg-visaro-orange/20 blur-3xl pointer-events-none" />

          <div className="relative flex flex-col gap-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-white/60 hover:text-white transition-colors duration-200 self-start"
            >
              <ArrowLeft size={16} weight="bold" />
              Back to search
            </button>

            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-widest text-white/50">
                International Flights
              </span>
              <h1 className="text-3xl font-bold text-white">Available Flights</h1>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {heroSlice && (
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/10">
                  <span className="font-aeonic text-xl font-bold text-white">{heroSlice.origin ?? "—"}</span>
                  <AirplaneInFlight size={16} weight="fill" className="text-visaro-orange" />
                  <span className="font-aeonic text-xl font-bold text-white">{heroSlice.destination ?? "—"}</span>
                  {heroDeparture && (
                    <>
                      <span className="w-px h-4 bg-white/15" />
                      <span className="text-sm font-medium text-white/70">
                        {moment(heroDeparture).format("D MMM YYYY")}
                      </span>
                    </>
                  )}
                </div>
              )}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 border border-white/10">
                <span className="text-sm font-medium text-white/70">
                  <span className="text-white font-semibold">{offers.length}</span> offer
                  {offers.length === 1 ? "" : "s"} available
                </span>
              </div>
            </div>
          </div>
        </div>

        {offers.length === 0 && (
          <div className="text-gray-500 border border-gray-200 rounded-2xl p-8 text-center bg-white shadow-[0_4px_20px_-4px_rgba(15,23,42,0.10)]">
            No flights found for this search. Try different dates or airports.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {offers.map((offer) => (
            <OfferCard key={offer.id} offer={offer} onSelect={onSelectOffer} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
